import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, Send, Sparkles, Briefcase, UserPlus, Lightbulb, Globe, Loader2, CheckCircle2, ClipboardCheck,
} from "lucide-react";
import api from "../../lib/api";

const CATEGORIES = [
  { id: "competitor_course", label: "Competitor Course", icon: Globe, blurb: "A program a competitor is promoting that we should look into." },
  { id: "contact_lead", label: "Contact / Lead", icon: UserPlus, blurb: "A uni rep, agent or partner contact worth saving." },
  { id: "feature_idea", label: "Feature Idea", icon: Lightbulb, blurb: "Improvement for the platform or our process." },
  { id: "other", label: "Other", icon: Sparkles, blurb: "Anything else worth sharing." },
];

const STATUS_BADGE = {
  new: "bg-blue-900/30 text-blue-300 border-blue-700/50",
  reviewed: "bg-amber-900/30 text-amber-300 border-amber-700/50",
  in_progress: "bg-violet-900/30 text-violet-300 border-violet-700/50",
  implemented: "bg-emerald-900/30 text-emerald-300 border-emerald-700/50",
  archived: "bg-stone-800 text-stone-400 border-stone-700",
};

const STATUS_LABEL = {
  new: "New",
  reviewed: "Reviewed",
  in_progress: "In Progress",
  implemented: "Implemented",
  archived: "Archived",
};

const EMPTY_FORM = {
  category: "competitor_course",
  title: "",
  description: "",
  url: "",
  contact: {
    full_name: "",
    role: "",
    organization: "",
    email: "",
    phone: "",
    linkedin_url: "",
    country: "",
    notes: "",
  },
};

/**
 * SubmitTipModal — submission form open to any active staff member.
 * After submit, the submitter is shown a brief acknowledgement and their
 * past tip list (title + status only — no content access by design).
 */
const SubmitTipModal = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [view, setView] = useState("form"); // 'form' | 'thanks'

  const fetchMine = async () => {
    try {
      const res = await api.get("/staff-tips/mine");
      setMine(res.data);
    } catch (_) {
      setMine([]);
    }
  };

  useEffect(() => {
    if (open) {
      fetchMine();
      setView("form");
    }
  }, [open]);

  if (!open) return null;

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setContact = (k, v) =>
    setForm((p) => ({ ...p, contact: { ...p.contact, [k]: v } }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) {
      toast.error("Please add a title.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        category: form.category,
        title: form.title.trim(),
        description: form.description.trim(),
        url: form.category === "competitor_course" ? form.url.trim() || null : null,
        contact: form.category === "contact_lead" ? form.contact : null,
      };
      await api.post("/staff-tips", payload);
      toast.success("Thanks — your tip has been shared with admins.");
      setForm(EMPTY_FORM);
      setView("thanks");
      fetchMine();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="submit-tip-modal">
      <div className="bg-[#0d0d0d] rounded-2xl border border-[#222] w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0d0d0d] border-b border-[#222] px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#CCFF00]" />
            <h2 className="text-base font-semibold text-white">Share an idea or contact</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1a1a1a] rounded-lg" data-testid="close-submit-tip">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {view === "thanks" ? (
          <ThanksView mine={mine} onSubmitAnother={() => setView("form")} />
        ) : (
          <form onSubmit={submit} className="p-6 space-y-5">
            <div className="bg-stone-900/40 border border-stone-800 rounded-xl p-3 text-xs text-stone-400 flex gap-2">
              <ClipboardCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#CCFF00]" />
              <div>
                Only super admins & hybrid reviewers can see what you submit.
                Once shared, you'll see only the title + status of your tip — not the
                content. Submit thoughtfully.
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Category</label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const active = form.category === c.id;
                  return (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => setField("category", c.id)}
                      className={`text-left p-3 rounded-xl border transition-colors ${
                        active
                          ? "border-[#CCFF00] bg-[#CCFF00]/5"
                          : "border-stone-800 bg-stone-900/40 hover:border-stone-700"
                      }`}
                      data-testid={`tip-category-${c.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-4 h-4 ${active ? "text-[#CCFF00]" : "text-slate-400"}`} />
                        <span className="text-sm font-semibold text-white">{c.label}</span>
                      </div>
                      <div className="text-[11px] text-slate-500">{c.blurb}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <FieldLabel label="Title *">
              <input
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="One-line summary of what you'd like to share"
                className="form-input"
                required
                data-testid="tip-title"
              />
            </FieldLabel>

            {/* Description */}
            <FieldLabel label="Description">
              <textarea
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                rows={4}
                placeholder="What did you find? Why is it interesting?"
                className="form-input resize-none"
                data-testid="tip-description"
              />
            </FieldLabel>

            {/* Category-specific */}
            {form.category === "competitor_course" && (
              <FieldLabel label="URL (competitor page)">
                <input
                  type="url"
                  value={form.url}
                  onChange={(e) => setField("url", e.target.value)}
                  placeholder="https://..."
                  className="form-input"
                  data-testid="tip-url"
                />
              </FieldLabel>
            )}

            {form.category === "contact_lead" && (
              <div className="space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Contact details</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FieldLabel label="Full name">
                    <input value={form.contact.full_name} onChange={(e) => setContact("full_name", e.target.value)} className="form-input" placeholder="Jane Doe" data-testid="contact-name"/>
                  </FieldLabel>
                  <FieldLabel label="Role / Title">
                    <input value={form.contact.role} onChange={(e) => setContact("role", e.target.value)} className="form-input" placeholder="Admissions Rep"/>
                  </FieldLabel>
                  <FieldLabel label="Organization / University">
                    <input value={form.contact.organization} onChange={(e) => setContact("organization", e.target.value)} className="form-input" placeholder="Metropolia UAS"/>
                  </FieldLabel>
                  <FieldLabel label="Country">
                    <input value={form.contact.country} onChange={(e) => setContact("country", e.target.value)} className="form-input" placeholder="Finland"/>
                  </FieldLabel>
                  <FieldLabel label="Email">
                    <input type="email" value={form.contact.email} onChange={(e) => setContact("email", e.target.value)} className="form-input" placeholder="jane@uni.fi"/>
                  </FieldLabel>
                  <FieldLabel label="Phone">
                    <input value={form.contact.phone} onChange={(e) => setContact("phone", e.target.value)} className="form-input" placeholder="+358 ..."/>
                  </FieldLabel>
                  <div className="sm:col-span-2">
                    <FieldLabel label="LinkedIn URL">
                      <input type="url" value={form.contact.linkedin_url} onChange={(e) => setContact("linkedin_url", e.target.value)} className="form-input" placeholder="https://linkedin.com/in/..." data-testid="contact-linkedin"/>
                    </FieldLabel>
                  </div>
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="flex justify-between items-center pt-4 border-t border-[#222]">
              <button type="button" onClick={onClose} className="px-5 py-2.5 bg-[#1a1a1a] hover:bg-[#222] text-slate-300 rounded-xl">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 bg-[#CCFF00] hover:bg-[#b8e600] text-black font-semibold rounded-xl disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="submit-tip-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit
              </button>
            </div>
          </form>
        )}

        {/* My past submissions (always shown — brief only) */}
        {mine.length > 0 && view === "form" && (
          <div className="border-t border-[#222] px-6 py-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">My past submissions</div>
            <MineList mine={mine} />
          </div>
        )}

        <style>{`
          .form-input { width:100%; padding:0.625rem 0.875rem; background:#0a0a0a; border:1px solid #2a2a2a; border-radius:0.625rem; color:white; font-size:0.875rem; }
          .form-input:focus { outline:none; border-color:#CCFF00; }
          .form-input::placeholder { color:#475569; }
        `}</style>
      </div>
    </div>
  );
};

const FieldLabel = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-400 mb-1.5">{label}</label>
    {children}
  </div>
);

const MineList = ({ mine }) => (
  <ul className="space-y-1.5 max-h-48 overflow-y-auto">
    {mine.map((t) => (
      <li
        key={t.id}
        className="flex items-center justify-between gap-3 bg-[#0a0a0a] border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm"
        data-testid={`mine-${t.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="text-white font-medium truncate">{t.title}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            {t.category.replace(/_/g, " ")} · {new Date(t.submitted_at).toLocaleDateString()}
          </div>
        </div>
        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded border whitespace-nowrap ${STATUS_BADGE[t.status] || ""}`}>
          {STATUS_LABEL[t.status] || t.status}
        </span>
      </li>
    ))}
  </ul>
);

const ThanksView = ({ mine, onSubmitAnother }) => (
  <div className="p-8 text-center" data-testid="tip-thanks-view">
    <div className="w-14 h-14 rounded-full bg-emerald-900/40 border border-emerald-700/40 flex items-center justify-center mx-auto mb-4">
      <CheckCircle2 className="w-7 h-7 text-emerald-400" />
    </div>
    <h3 className="font-outfit text-xl font-bold text-white mb-1">Thanks for sharing!</h3>
    <p className="text-sm text-slate-400 mb-6 max-w-sm mx-auto">
      Admins have been notified. You can see only the title and status of your tip from
      here on — you won't be able to view the content again.
    </p>

    {mine.length > 0 && (
      <div className="text-left">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Your submissions</div>
        <MineList mine={mine} />
      </div>
    )}

    <button
      onClick={onSubmitAnother}
      className="mt-6 px-5 py-2.5 bg-[#1a1a1a] hover:bg-[#222] text-slate-200 rounded-xl text-sm font-medium"
    >
      Submit another
    </button>
  </div>
);

export default SubmitTipModal;
